// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import useAuth from './hooks/useAuth';
import StandardsDashboard from './StandardsDashboard';
import Onboarding from './Onboarding';
import SettingsPage from './pages/SettingsPage';
import TeamPage from './pages/TeamPage';
import PricingPage from './pages/PricingPage';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancelled from './pages/PaymentCancelled';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ContactPage from './pages/ContactPage';
import WelcomePage from './pages/WelcomePage';

// --- 1. IMPORT THE NEW LANDING PAGE ---
import LandingPage from './pages/LandingPage'; 
// -------------------------------------

import './App.css';

const API_BASE_URL = window.location.hostname === 'localhost' ? 'http://localhost:7071' : '';

// This is the layout for LOGGED-IN users
function AppLayout({ user, customerConfig }) {
    return (
        <div className="App">
            <header className="App-header">
                <h1>Compliance Guardian</h1>
                <div className="user-info">
                    <span>Welcome, {user.userDetails}!</span>
                    <nav>
                        <Link to="/">Dashboard</Link>
                        <Link to="/team">Team</Link>
                        <Link to="/settings">Settings</Link>
                        <Link to="/pricing">Pricing</Link> 
                    </nav>
                    <a href="/.auth/logout" className="button-logout">Logout</a>
                </div>
            </header>
            <main className="App-main">
                <Routes>
                    <Route path="/" element={<StandardsDashboard user={user} />} />
                    <Route path="/team" element={<TeamPage />} />
                    <Route path="/settings" element={<SettingsPage initialConfig={customerConfig} />} />
                    <Route path="/pricing" element={<PricingPage />} />
                    <Route path="/welcome" element={<WelcomePage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/payment-success" element={<PaymentSuccess />} />
                    <Route path="/payment-cancelled" element={<PaymentCancelled />} />
                    <Route path="*" element={<Navigate to="/" />} />
                </Routes>
            </main>
            <footer className="site-footer" aria-label="Footer">
                <div className="footer-inner">
                    <div className="footer-brand">
                        <div className="footer-title">Compliance Guardian</div>
                        <p className="footer-tagline">Continuous HIPAA compliance for Microsoft Cloud.</p>
                    </div>
                    <nav className="footer-nav" aria-label="Footer navigation">
                        <div className="footer-column">
                            <div className="footer-heading">Navigate</div>
                            <ul className="footer-links">
                                <li><Link to="/">Dashboard</Link></li>
                                <li><Link to="/team">Team</Link></li>
                                <li><Link to="/settings">Settings</Link></li>
                                <li><Link to="/pricing">Pricing</Link></li>
                            </ul>
                        </div>
                        <div className="footer-column">
                            <div className="footer-heading">Legal</div>
                            <ul className="footer-links">
                                <li><Link to="/privacy">Privacy</Link></li>
                                <li><Link to="/terms">Terms</Link></li>
                            </ul>
                        </div>
                        <div className="footer-column">
                            <div className="footer-heading">Contact</div>
                            <ul className="footer-links">
                                <li><Link to="/contact">Contact Us</Link></li>
                                <li><a href="mailto:support@example.com">support@example.com</a></li>
                            </ul>
                        </div>
                    </nav>
                </div>
                <div className="footer-bottom">
                    <div className="footer-copy">© {new Date().getFullYear()} Compliance Guardian. All rights reserved.</div>
                </div>
            </footer>
        </div>
    );
}

// This is the simple layout for the PUBLIC (logged-out) pages
function PublicLayout() {
    return (
        <div className="App">
            <header className="App-header">
                <h1>Compliance Guardian</h1>
                <div className="user-info">
                    <nav>
                        <Link to="/">Home</Link>
                        <Link to="/welcome">Why Us</Link>
                        <Link to="/pricing">Pricing</Link>
                    </nav>
                    <a href="/.auth/login/aad" className="button">Login</a>
                </div>
            </header>
            <main className="App-main">
                <Routes>
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/pricing" element={<PricingPage />} />
                    <Route path="/welcome" element={<WelcomePage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="*" element={<Navigate to="/" />} />
                </Routes>
            </main>
        </div>
    );
}


function App() {
    const { user, isLoading: isAuthLoading } = useAuth();
    const [appState, setAppState] = useState('loading');
    const [customerConfig, setCustomerConfig] = useState(null);

    useEffect(() => {
        if (isAuthLoading) {
            setAppState('loading');
            return;
        }
        if (!user) {
            setAppState('logged_out');
            return;
        }
        const checkUserStatus = async () => {
            const maxRetries = 3;
            let attempt = 0;
            while (attempt < maxRetries) {
                attempt++;
                try {
                    const response = await fetch(`${API_BASE_URL}/api/user/status`);
                    if (response.ok) {
                        const data = await response.json();
                        setCustomerConfig(data);
                        setAppState('onboarded');
                        return;
                    } else if (response.status === 404) {
                        setAppState('needs_onboarding');
                        return;
                    }
                    throw new Error(`API returned status ${response.status}`);
                } catch (error) {
                    console.warn(`Attempt ${attempt} to check user status failed:`, error);
                    if (attempt >= maxRetries) {
                        setAppState('error');
                        return;
                    }
                    await new Promise(resolve => setTimeout(resolve, 2000));
                }
            }
        };
        checkUserStatus();
    }, [user, isAuthLoading]);

    const handleOnboardingSuccess = () => {
        // Instead of reloading, we just change the state to move to the pricing page
        // We will assume the user now needs to subscribe, so we'll route them to the main app
        // which will then show the pricing page.
        // A full reload is still safest to reset all state.
        window.location.reload(); 
    };

    if (appState === 'loading') {
        return <div className="App-center"><h1>Compliance Guardian</h1><p>Loading Your Workspace...</p></div>;
    }

    // --- 2. MODIFIED LOGGED_OUT STATE ---
    // This now returns a full Router for the public-facing website
    if (appState === 'logged_out') {
        return (
            <Router>
                <PublicLayout />
            </Router>
        );
    }
    // ------------------------------------
    
    // This is the Router for AUTHENTICATED users
    return (
        <Router>
            {appState === 'needs_onboarding' && (
                <div className="App">
                    <header className="App-header"><h1>Compliance Guardian</h1><div className="user-info"><span>{user.userDetails}</span><a href="/.auth/logout" className="button-logout">Logout</a></div></header>
                    <main className="App-main"><Onboarding user={user} onOnboardingSuccess={handleOnboardingSuccess} /></main>
                </div>
            )}
            {appState === 'onboarded' && customerConfig && (
                <AppLayout user={user} customerConfig={customerConfig} />
            )}
            {appState === 'error' && <div className="App-center"><p className="error">Could not load your configuration. Please try refreshing the page.</p></div>}
        </Router>
    );
}

export default App;
